import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomerDetailsStyles from './styles/CustomerDetails.module.scss';
import styles from './styles/MyInfoIban.module.scss';

import Loading from '../Common/Loading';
import Button from '../Common/Button';

// Actions.
import { postBankAccount } from '../../actions/contactForm';
// Types.
import { SimpleUser } from '../../types/customer';
import { Ledger } from '../../types/billing';
// Utils.
import { gaEventIfAllowed } from '../../utils/googleAnalytics';
import { validateIban } from '../../utils/contact';

type IbanProps = {
  customer: SimpleUser;
  ledgers: Ledger[];
};

const MyInfoIban: React.FunctionComponent<IbanProps> = (props: IbanProps) => {
  const [iban, setIban] = React.useState<string>('');
  const [validIban, setValidIban] = React.useState<boolean>(false);
  const [updateIbanData, setUpdateIbanData] = React.useState<boolean>(false);
  const [sendingData, setSendingData] = React.useState<boolean>(false);
  const [dataUpdated, setDataUpdated] = React.useState<boolean>(false);
  const [updateError, setUpdateError] = React.useState<boolean>(false);
  const { customer, ledgers } = props;
  const { t } = useTranslation();

  React.useEffect(() => {
    const ledgerWithIban = ledgers && ledgers.length > 0 ? ledgers.find((l) => l.iban) : undefined;

    if (ledgerWithIban) {
      const ledgerIban = ledgerWithIban.iban ?? '';
      setIban(ledgerIban);
      setValidIban(validateIban(ledgerIban));
      setUpdateIbanData(false);
    } else {
      setUpdateIbanData(true);
    }
  }, [customer, ledgers]);

  async function updateIbanNumber(): Promise<void> {
    gaEventIfAllowed({
      category: 'Tilinumeron päivittäminen',
      action: 'Tallenna tilinumero',
    });

    if (customer && validIban) {
      const customerId = customer.userId;
      const firstName = customer.userFirstName;
      const lastName = customer.userLastName;

      const contactInfo = {
        firstName: firstName,
        lastName: lastName,
        email: customer?.userEmail ?? '',
        phone: customer?.userNumber ?? '',
        message: `Tilinumero hyvitystä varten: ${iban}`,
      };

      setSendingData(true);

      const updatedUserData = await postBankAccount(contactInfo, customerId);

      if (updatedUserData.status === 200) {
        setUpdateError(false);
        setSendingData(false);
        setDataUpdated(true);
        setUpdateIbanData(false);
      } else {
        setUpdateError(true);
      }
    }
  }

  const updateForm = (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const { target } = event;
    const { value } = target;

    const upperCaseValue = value.toUpperCase();

    setValidIban(validateIban(upperCaseValue));
    setIban(upperCaseValue);
  };

  const customerId = customer.userId;

  const ibanWithSpaces = iban.match(/.{1,4}/g);

  return (
    <div className={CustomerDetailsStyles.address}>
      <form onSubmit={updateIbanNumber}>
        <div className="col-12">
          <h3>{t('myInfo:customerInfo.bankDetails')}</h3>
          {!updateIbanData && iban !== '' && (
            <p>
              {t('myInfo:customerInfo.bankAccount')}: {ibanWithSpaces?.join(' ')}
            </p>
          )}
          {updateIbanData && (
            <>
              <label htmlFor={`user-${customerId}-iban`} className={styles.label}>
                {t('myInfo:customerInfo.bankAccount')}
              </label>
              <input
                type="text"
                name="phone"
                id={`user-${customerId}-iban`}
                value={iban}
                onChange={updateForm}
                onBlur={updateForm}
                maxLength={18}
                required
              />
              {!validIban && (
                <strong className={styles.error} aria-live="polite">
                  {t('myInfo:customerInfo.invalidIban')}
                </strong>
              )}
            </>
          )}
        </div>
        {!updateIbanData && (
          <Button
            type="button"
            className="buttonWhite"
            onClick={(): void => {
              setUpdateIbanData(true);
            }}
          >
            {t('myInfo:customerInfo.updateBankDetails')}
          </Button>
        )}
        {sendingData && <Loading size="small" />}
        {!dataUpdated && iban === '' && <p aria-live="polite">{t('myInfo:customerInfo.updateBankDetailsFewDays')}</p>}
        {dataUpdated && <p aria-live="polite">{t('myInfo:customerInfo.updateBankDetailsSuccess')}</p>}
        {updateError && <p aria-live="polite">{t('common:messages.updateError')}</p>}
        {updateIbanData && (
          <>
            <Button
              type="submit"
              disabled={!validIban}
              onClick={(e): void => {
                e.preventDefault();
                if (validIban) {
                  updateIbanNumber();
                }
              }}
            >
              {t('myInfo:customerInfo.updateBankDetailsSubmit')}
            </Button>
            {ledgers && ledgers.length > 0 && ledgers.filter((l) => l.iban).length > 0 && (
              <Button type="button" className="buttonWhite" onClick={(): void => setUpdateIbanData(false)}>
                {t('common:general.cancel')}
              </Button>
            )}
          </>
        )}
      </form>
    </div>
  );
};

export default MyInfoIban;
